import { Sides } from "@project/emerge-types";
import { Easing } from "@tweenjs/tween.js";
import { Vector3 } from "three";
import { ConnectorCategory } from "../enum/ConnectorType";
import type { ConnectorInfos } from "../interfaces/ConnectorInfos";
import type { CutProperties } from "../interfaces/CutProperties";
import type {
  EasingFunction,
  GroupAnimationProperties,
} from "../interfaces/GroupAnimationProperties";
import { RectPropertiesBySide } from "../structure/RectProperties";

/**
 * Upper bound (mm) for how far a lid, hood or door travels when it opens.
 *
 * The travel is derived from the inner dimension along the movement axis, so
 * the lid clears the opening completely and leaves an unobstructed view of the
 * interior. On a very large case that distance would push the lid so far that
 * the fitted camera has to zoom out until the case itself becomes tiny, so the
 * proportional travel is capped here.
 */
export const LID_LINEAR_TRAVEL_MAX_MM = 500;

const LID_LINEAR_TRAVEL_FACTOR = 1;

/**
 * Gap (mm) that stays between the moving part and the opening it was cut from.
 * It puts a floor under the travel: a deep lid must clear its own depth before
 * anything of the interior becomes visible, which the proportional travel alone
 * does not guarantee on a very small case or once the cap above kicks in.
 */
const LID_LINEAR_TRAVEL_CLEARANCE_MM = 50;

/** Fallback inner dimension (mm) when the blueprint has none yet. */
const DEFAULT_RELEVANT_DIMENSION = 200;

/** Sides of the hinge connectors that belong to one cover group. */
function hingeSidesForGroup(
  connectors: ConnectorInfos[],
  groupName: string,
): Set<Sides> {
  const sides = new Set<Sides>();
  for (const connector of connectors) {
    if (
      connector.group === groupName &&
      connector.connectorType === ConnectorCategory.Hinge
    ) {
      sides.add(connector.side);
    }
  }
  return sides;
}

function calculateLinearTravel(
  animationDirection: Vector3,
  innerDimensions?: Vector3,
  coverDepth = 0,
): number {
  const relevantDimension = innerDimensions
    ? Math.abs(animationDirection.x) * innerDimensions.x +
      Math.abs(animationDirection.y) * innerDimensions.y +
      Math.abs(animationDirection.z) * innerDimensions.z
    : DEFAULT_RELEVANT_DIMENSION;

  const proportionalTravel = Math.min(
    relevantDimension * LID_LINEAR_TRAVEL_FACTOR,
    LID_LINEAR_TRAVEL_MAX_MM,
  );

  return Math.max(
    proportionalTravel,
    coverDepth + LID_LINEAR_TRAVEL_CLEARANCE_MM,
  );
}

/**
 * Generiert GroupAnimationProperties-Array aus Connector-Informationen
 * Rotationspunkte werden später in updateGroupAnimationRotationPoints() berechnet
 * Scharniere haben Vorrang vor Schlössern in derselben Gruppe
 */
export function calculateGroupAnimations(
  connectors: ConnectorInfos[],
  cuts: CutProperties[] = [],
  innerDimensions?: Vector3,
): GroupAnimationProperties[] {
  const groupAnimations: GroupAnimationProperties[] = [];
  const processedGroups = new Set<string>();

  // Sammle alle Gruppen mit Scharnieren
  const groupsWithHinges = new Set<string>();
  for (const connector of connectors) {
    if (
      connector.group &&
      connector.connectorType === ConnectorCategory.Hinge
    ) {
      groupsWithHinges.add(connector.group);
    }
  }

  // Verarbeite alle Cuts/Gruppen
  for (const cut of cuts) {
    if (!cut.groupName || processedGroups.has(cut.groupName)) {
      continue;
    }

    let animationInfo: GroupAnimationProperties | null = null;

    if (groupsWithHinges.has(cut.groupName)) {
      // Scharniere auf verschiedenen Kanten haben keine gemeinsame Achse.
      // Der Deckel bleibt zu, statt um einen gemittelten Punkt zu kippen.
      if (hingeSidesForGroup(connectors, cut.groupName).size > 1) {
        processedGroups.add(cut.groupName);
        continue;
      }
      // Gruppe hat Scharniere -> Rotationsanimation
      const hingeConnector = connectors.find(
        (c) =>
          c.group === cut.groupName &&
          c.connectorType === ConnectorCategory.Hinge,
      );
      if (hingeConnector) {
        animationInfo = createAnimationInfoFromConnector(
          hingeConnector,
          cuts,
          innerDimensions,
        );
      }
    } else {
      // Keine Scharniere -> Sliding-Animation basierend auf Cut
      animationInfo = createAnimationInfoFromCut(cut, innerDimensions);
    }

    if (animationInfo) {
      groupAnimations.push(animationInfo);
      processedGroups.add(cut.groupName);
    }
  }

  return groupAnimations;
}

/**
 * Aktualisiert die Rotationspunkte für Scharniere basierend auf den berechneten Part-Positionen.
 * Diese Funktion sollte nach den BuildSteps aufgerufen werden.
 *
 * Scharnier-Versatz (hingeOffset, z.B. 3.75 mm bei SHA2524): Der Rotationspunkt liegt
 * hingeOffset vom Kasten (Profilkante) in Richtung der Platten-Normale (Deckel/Klappe).
 * So rotiert die Klappe um die tatsächliche Scharnierachse.
 */
export function updateGroupAnimationRotationPoints(
  groupAnimations: GroupAnimationProperties[],
  hingeDefinitions: Array<{
    group?: string;
    position: Vector3;
    partSelection?: { hingeOffset?: number };
    side?: Sides;
    edgeSide?: Sides;
  }>,
): GroupAnimationProperties[] {
  const result = groupAnimations.map((animation) => {
    if (animation.animationType === "rotation") {
      // Finde alle Scharniere dieser Gruppe
      const groupHinges = hingeDefinitions.filter(
        (hinge) => hinge.group === animation.groupName,
      );

      if (groupHinges.length > 0) {
        // Berechne den Mittelpunkt aller Scharniere als Basis-Rotationspunkt
        const baseRotationPoint = new Vector3();
        groupHinges.forEach((hinge) => {
          baseRotationPoint.add(hinge.position);
        });
        baseRotationPoint.divideScalar(groupHinges.length);

        // Scharnier-Versatz: Rotationspunkt = Kante + hingeOffset in Richtung Platten-Normale
        const adjustedRotationPoint = baseRotationPoint.clone();
        const firstHinge = groupHinges[0];
        const side = firstHinge?.side ?? firstHinge?.edgeSide;
        const hingeOffset = firstHinge?.partSelection?.hingeOffset;

        if (hingeOffset != null && hingeOffset !== 0 && side !== undefined) {
          const rectProps = RectPropertiesBySide[side];
          if (rectProps) {
            const offsetDirection = rectProps.normalDir.clone();
            adjustedRotationPoint.add(
              offsetDirection.multiplyScalar(hingeOffset),
            );
          }
        }

        return {
          ...animation,
          rotationPoint: {
            x: adjustedRotationPoint.x,
            y: adjustedRotationPoint.y,
            z: adjustedRotationPoint.z,
          },
        };
      }
    }

    return animation;
  });

  return result;
}

/**
 * Erstellt AnimationInfo basierend auf einem einzelnen Connector
 */
function createAnimationInfoFromConnector(
  connector: ConnectorInfos,
  cuts: CutProperties[] = [],
  innerDimensions?: Vector3,
): GroupAnimationProperties | null {
  if (!connector.group) {
    return null;
  }

  // Finde den zugehörigen Cut für diese Gruppe
  const cut = cuts.find((c) => c.groupName === connector.group);

  const isHinge = connector.connectorType === ConnectorCategory.Hinge;

  // Verwende RectPropertiesBySide für Richtungen
  let animationDirection: Vector3;
  let movementDirection: Vector3 | undefined;

  if (isHinge) {
    const cutRef = cut?.reference || connector.side;
    animationDirection = getRotationAxisFromSideAndCut(connector.side, cutRef);
    movementDirection = getMovementDirectionFromSide(cutRef);
  } else {
    // Für alle anderen Connector-Typen (Schlösser, etc.): Sliding-Animation
    animationDirection = getMovementDirectionFromSide(
      cut?.reference || connector.side,
    );
  }

  // Invertiere Richtungen wenn invertCover=true. Bei Scharnieren ist
  // `animationDirection` die Rotationsachse, die nicht gedreht werden darf -
  // dort wird nur die Bewegungsrichtung invertiert. Ohne Scharnier ist
  // `animationDirection` selbst die Schieberichtung, sonst fahren invertierte
  // Deckel (z. B. "Boden" der Haube) nach oben durch das Case statt nach unten.
  if (cut?.invertCover) {
    if (movementDirection) {
      movementDirection.negate();
    } else {
      animationDirection.negate();
    }
  }

  // Bestimme Animationsparameter basierend auf Connector-Typ
  const duration = isHinge ? 900 : 800; // Scharniere langsamer als Schlösser
  const easing: EasingFunction = isHinge
    ? Easing.Bounce.Out
    : Easing.Quartic.Out;

  const animationInfo: GroupAnimationProperties = {
    groupName: connector.group,
    animationType: isHinge ? "rotation" : "linear",
    animationDirection: {
      x: animationDirection.x,
      y: animationDirection.y,
      z: animationDirection.z,
    },

    // Animationsparameter
    duration,
    easing,
  };

  // Füge Bewegungsrichtung für Scharniere hinzu
  if (movementDirection) {
    animationInfo.movementDirection = {
      x: movementDirection.x,
      y: movementDirection.y,
      z: movementDirection.z,
    };
  }

  // Setze Standard-Werte für Animation
  if (isHinge) {
    animationInfo.maxAngle = (Math.PI * 2) / 3; // 120 Grad
  } else {
    animationInfo.maxDistance = calculateLinearTravel(
      animationDirection,
      innerDimensions,
      cut?.offset,
    );
  }

  return animationInfo;
}

/**
 * Erstellt AnimationInfo für Cuts ohne Connectors (Standard: Sliding-Animation)
 */
function createAnimationInfoFromCut(
  cut: CutProperties,
  innerDimensions?: Vector3,
): GroupAnimationProperties | null {
  if (!cut.groupName) {
    return null;
  }

  // Für Cuts ohne Connectors immer Sliding-Animation verwenden
  const animationDirection = getMovementDirectionFromSide(cut.reference);

  // Invertiere Richtung wenn invertCover=true
  if (cut.invertCover) {
    animationDirection.negate();
  }

  // Standard-Parameter für Sliding-Animation
  const duration = 800; // Standarddauer für Sliding
  const easing: EasingFunction = Easing.Quartic.Out;

  const maxDistance = calculateLinearTravel(
    animationDirection,
    innerDimensions,
    cut.offset,
  );

  const animationInfo: GroupAnimationProperties = {
    groupName: cut.groupName,
    animationType: "linear",
    animationDirection: {
      x: animationDirection.x,
      y: animationDirection.y,
      z: animationDirection.z,
    },
    duration,
    easing,
    maxDistance,
  };

  return animationInfo;
}

/**
 * Berechnet die Rotationsachse für Scharniere basierend auf der Seite des Connectors und der Cut-Reference
 * Die Rotationsachse ist die Richtung, in der das Scharnier verläuft (senkrecht zur Öffnungsrichtung)
 */
function getRotationAxisFromSideAndCut(
  connectorSide: Sides,
  cutReference: Sides,
): Vector3 {
  if (cutReference === Sides.TOP || cutReference === Sides.BOTTOM) {
    const matrix: Partial<Record<Sides, Vector3>> = {
      [Sides.FRONT]: new Vector3(1, 0, 0),
      [Sides.BACK]: new Vector3(-1, 0, 0),
      [Sides.LEFT]: new Vector3(0, 0, 1),
      [Sides.RIGHT]: new Vector3(0, 0, -1),
    };
    return matrix[connectorSide] || new Vector3(1, 0, 0);
  }

  if (cutReference === Sides.FRONT || cutReference === Sides.BACK) {
    const matrix: Partial<Record<Sides, Vector3>> = {
      [Sides.TOP]: new Vector3(-1, 0, 0),
      [Sides.BOTTOM]: new Vector3(1, 0, 0),
      [Sides.LEFT]: new Vector3(0, -1, 0),
      [Sides.RIGHT]: new Vector3(0, 1, 0),
    };
    const axis = matrix[connectorSide];
    return axis &&
      cutReference === Sides.BACK &&
      (connectorSide === Sides.TOP || connectorSide === Sides.BOTTOM)
      ? axis.clone().negate()
      : axis || new Vector3(1, 0, 0);
  }

  if (cutReference === Sides.LEFT || cutReference === Sides.RIGHT) {
    const matrix: Partial<Record<Sides, Vector3>> = {
      [Sides.TOP]: new Vector3(0, 0, 1),
      [Sides.BOTTOM]: new Vector3(0, 0, -1),
      [Sides.FRONT]: new Vector3(0, 1, 0),
      [Sides.BACK]: new Vector3(0, -1, 0),
    };
    const axis = matrix[connectorSide];
    return axis &&
      cutReference === Sides.RIGHT &&
      (connectorSide === Sides.TOP || connectorSide === Sides.BOTTOM)
      ? axis.clone().negate()
      : axis || new Vector3(1, 0, 0);
  }

  return new Vector3(1, 0, 0);
}

/**
 * Berechnet die Bewegungsrichtung basierend auf Cut-Reference (Normale der Seite)
 * Verwendet RectPropertiesBySide für konsistente Richtungen
 */
function getMovementDirectionFromSide(cutReference: Sides): Vector3 {
  const rectProps = RectPropertiesBySide[cutReference];
  if (!rectProps) {
    return new Vector3(0, 1, 0); // Fallback
  }

  // Verwende die normale Richtung der Seite
  return rectProps.normalDir.clone();
}
